import {
  useDataProvider,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";

import { notifyMutationError } from "@/lib/repository-utils";
import { ShowGenreEditFormType } from "@/pages/show-genres/schemas/show-genre.edit.form";

export interface ShowGenreSummary {
  id: string;
  name: string;
}

/** The four locales `TransliterationsDbDto` requires — every one must be a list, never null. */
const TRANSLITERATION_LOCALES = ["en", "el", "ar", "ro"] as const;

/**
 * ShowGenre repository — search over the show-genres Typesense explore endpoint
 * via Refine getList, mirroring useGenreRepository. Used by the movie genre
 * picker. Note the resource name is the hyphenated `show-genres`, matching the
 * Admin API route group `api/show-genres`.
 */
export const useShowGenreRepository = () => {
  const { getList, getOne } = useDataProvider()();
  const { mutateAsync: updateAsync } = useUpdate({ resource: "show-genres" });
  const { edit } = useNavigation();
  const { open } = useNotification();

  async function search(query: string): Promise<ShowGenreSummary[]> {
    if (!query) return [];
    try {
      const response = await getList({
        resource: "show-genres",
        filters: [],
        pagination: { currentPage: 0, pageSize: 25 },
        meta: { sortBy: "_text_match:desc", query, queryBy: "name" },
      });
      return response.data.map((hit: any) => ({
        id: hit?.document?.id ?? hit?.id,
        name: hit?.document?.name ?? hit?.name,
      }));
    } catch (error) {
      console.error("show-genres search failed", error);
      notifyMutationError(open, "Show genre search", error);
      return [];
    }
  }

  async function get(id: string): Promise<unknown> {
    return getOne({ id, resource: "show-genres" });
  }

  /**
   * Partial update onto `UpdateShowGenreCommand`. The form's `inCinemas`/`inTheaters` pass
   * straight through (the command speaks booleans, not `sections`); `slug`, the enrichment
   * lifecycle and the audit stamps are not command members and are never sent.
   */
  async function update(values: Partial<ShowGenreEditFormType>) {
    const payload: Record<string, unknown> = { id: values.id };
    for (const key of [
      "name",
      "about",
      "aliases",
      "parents",
      "inCinemas",
      "inTheaters",
      "externalIds",
    ] as const) {
      if (values[key] !== undefined) payload[key] = values[key];
    }
    if (values.transliterations !== undefined) {
      payload.transliterations = values.transliterations
        ? Object.fromEntries(
            TRANSLITERATION_LOCALES.map((locale) => [
              locale,
              values.transliterations?.[locale] ?? [],
            ]),
          )
        : null;
    }

    const response = await updateAsync?.(
      { id: values.id!, values: payload },
      {
        onSuccess: () => {
          edit("show-genres", values.id!, "replace");
          open?.({
            type: "success",
            message: "Successfully updated show genre",
            description: `You updated ${values.name ?? "a show genre"}`,
          });
        },
        onError: (error) => {
          console.error(
            "Failed to update show genre with values",
            values,
            error,
          );
          notifyMutationError(open, "Update show genre request", error);
        },
      },
    );

    return response?.data.id?.toString();
  }

  return { search, get, update };
};
